import { useT } from '@transifex/react'
import React, { FunctionComponent, useEffect, useRef } from 'react'
import { Autocomplete, Text } from '@lightspeed/design-system-react'
import { isIOS } from '@vend/utilities'
import {
  useHotkeysService,
  useRegisterManager,
  useTrainingModeManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../ng-compat/hooks'
import { PERMISSIONS } from '../../constants'
import {
  ProductDetails,
  useAddProductToSale,
} from '../AddProductToSale/useAddProductToSale'
import { $rootScope } from '../../ng-compat/imports'
import {
  HydratedSuggestion,
  WrReactProductSearch,
} from './WrReactProductSearch'
import { useGlobalSearchFeelingLucky } from './useGlobalSearchFeelingLucky'

const HOT_KEY = 's'

export const SaleProductSearch: FunctionComponent = () => {
  const t = useT()

  const userManager = useUserManager()
  const registerManager = useRegisterManager()
  const trainingModeManager = useTrainingModeManager()
  const hotkeysService = useHotkeysService()
  const ref = useRef<Autocomplete<HydratedSuggestion>>(null)
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const feelingLucky = useGlobalSearchFeelingLucky()

  const {
    addProductToSale,
    createNewProductBySearchTerm,
  } = useAddProductToSale()
  const canCreateNewProduct =
    webRegisterStatusManager.isOnline() &&
    !trainingModeManager.trainingModeEnabled &&
    userManager.hasPermission(PERMISSIONS.PRODUCT_ADD_EDIT) &&
    registerManager.isActiveRegisterOpen()

  const focusOnSearch = () => {
    ref.current?.focusAndSelectInput()
  }

  useEffect(() => {
    focusOnSearch()
    const deregisterEvent = $rootScope.$on(
      'autocomplete:global-search:focus',
      (_, options: { focusDelay: number } = { focusDelay: 0 }) => {
        isIOS()
          ? focusOnSearch()
          : setTimeout(focusOnSearch, options.focusDelay)
      }
    )
    return () => {
      deregisterEvent()
    }
  }, [])

  useEffect(() => {
    const config = {
      summary: 'Focus product search',
      combo: `alt+${HOT_KEY}`,
      count: true,
      action: () => focusOnSearch(),
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [hotkeysService])

  const onAddNewProduct = (searchTerm: string) => {
    canCreateNewProduct && createNewProductBySearchTerm(searchTerm)
  }

  const onProductSelected = (details: ProductDetails | null) => {
    if (details) {
      addProductToSale(details)
    }
  }

  return (
    <>
      <div className="vd-mb1">
        <Text type="label">
          {t('Search for products', { _context: 'Title' })}
        </Text>
      </div>
      <div data-hotkey-tip={`${HOT_KEY.toUpperCase()}`}>
        <WrReactProductSearch
          ref={ref}
          onProductSelected={onProductSelected}
          feelingLucky={feelingLucky}
          onAddNewProduct={onAddNewProduct}
        />
      </div>
    </>
  )
}
